import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Monitor, Clock, MapPin, Briefcase, CheckCircle, MessageCircle, Zap, Home } from 'lucide-react';
import { Button } from '../components/ui/button';

const OnlineTrainingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="online-page" data-testid="online-training-page">

      {/* Hero */}
      <section className="online-hero">
        <div className="online-hero-content">
          <span className="online-tag"><Monitor size={16} /> Live 1-on-1 Zoom Sessions</span>
          <h1>Your Personal Trainer.<br />Wherever You Are.</h1>
          <p>No commute. No gym. No excuses. Get the same expert coaching, accountability and results — all from your living room, home office, or hotel room.</p>
          <div className="online-hero-btns">
            <a href="https://wa.me/447787166065?text=Hi%20Aaron%2C%20I%27m%20interested%20in%20online%20Zoom%20training%20sessions" target="_blank" rel="noopener noreferrer" className="online-cta-primary" data-testid="online-hero-cta">
              <MessageCircle size={18} /> Book a Free Call
            </a>
            <Button onClick={() => navigate('/nutrition-guide')} className="online-cta-secondary">Get Free Nutrition Guide</Button>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="online-section">
        <div className="online-container">
          <h2 className="online-section-title">Sound familiar?</h2>
          <div className="online-pain-grid">
            {[
              { icon: <Briefcase size={24} />, title: "You're stuck at a desk all day", text: "Back-to-back meetings, long commutes, and by the time you're home the last thing you want is to drive to a gym. Your body's paying the price for your career." },
              { icon: <Clock size={24} />, title: "You don't have time to waste", text: "Between work, family and everything else, you need something that fits into your schedule — not the other way around. An hour here and there is all you've got." },
              { icon: <MapPin size={24} />, title: "The gym feels intimidating or inconvenient", text: "Maybe you travel for work, maybe the nearest decent gym is 30 minutes away, or maybe you just prefer training in your own space. That's completely fine." },
              { icon: <Home size={24} />, title: "You've tried doing it alone", text: "You've downloaded apps, watched YouTube videos, maybe even bought some dumbbells. But without structure and someone keeping you accountable, nothing sticks." }
            ].map((item, i) => (
              <div key={i} className="online-pain-card">
                <div className="online-pain-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="online-section online-section-alt">
        <div className="online-container">
          <h2 className="online-section-title">How Online Training Works</h2>
          <p className="online-section-sub">It's simpler than you think. All you need is a phone or laptop and a bit of floor space.</p>
          <div className="online-steps">
            {[
              { num: "1", title: "We chat", text: "A quick free call to understand your goals, schedule and what equipment (if any) you have at home." },
              { num: "2", title: "I build your plan", text: "A structured programme designed around your goals, your available time, and your space — whether that's a full home gym or just a yoga mat." },
              { num: "3", title: "We train live on Zoom", text: "Real-time coaching, form correction, motivation. It's like having me in the room with you. Every rep, every set, every session." },
              { num: "4", title: "You see results", text: "With consistent sessions and nutrition guidance, you'll see and feel the difference within weeks — not months." }
            ].map((s, i) => (
              <div key={i} className="online-step-card">
                <span className="online-step-num">{s.num}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="online-section">
        <div className="online-container">
          <h2 className="online-section-title">Simple, Honest Pricing</h2>
          <p className="online-section-sub">No contracts. No hidden fees. Just results.</p>
          <div className="online-pricing-grid">
            <div className="online-price-card">
              <h3>Starter</h3>
              <div className="online-price"><span className="online-price-amount">£40</span><span className="online-price-per">/session</span></div>
              <p className="online-price-freq">Minimum 2 sessions per week</p>
              <ul className="online-price-features">
                <li><CheckCircle size={16} /> Live 1-on-1 Zoom sessions</li>
                <li><CheckCircle size={16} /> Personalised training programme</li>
                <li><CheckCircle size={16} /> Form correction in real-time</li>
                <li><CheckCircle size={16} /> WhatsApp support between sessions</li>
                <li><CheckCircle size={16} /> Nutrition guidance</li>
              </ul>
              <p className="online-price-total">From <strong>£320/month</strong></p>
              <a href="https://wa.me/447787166065?text=Hi%20Aaron%2C%20I%27m%20interested%20in%20the%20online%20Starter%20plan%20(2%20sessions%2Fweek)" target="_blank" rel="noopener noreferrer" className="online-price-btn">
                <MessageCircle size={16} /> Get Started
              </a>
            </div>
            <div className="online-price-card online-price-featured">
              <span className="online-price-badge">Best Value</span>
              <h3>Committed</h3>
              <div className="online-price"><span className="online-price-amount">£35</span><span className="online-price-per">/session</span></div>
              <p className="online-price-freq">Minimum 3 sessions per week</p>
              <ul className="online-price-features">
                <li><CheckCircle size={16} /> Everything in Starter</li>
                <li><CheckCircle size={16} /> Priority scheduling</li>
                <li><CheckCircle size={16} /> Weekly progress check-ins</li>
                <li><CheckCircle size={16} /> Detailed meal planning</li>
                <li><CheckCircle size={16} /> Faster results, more accountability</li>
              </ul>
              <p className="online-price-total">From <strong>£420/month</strong></p>
              <a href="https://wa.me/447787166065?text=Hi%20Aaron%2C%20I%27m%20interested%20in%20the%20online%20Committed%20plan%20(3%20sessions%2Fweek)" target="_blank" rel="noopener noreferrer" className="online-price-btn online-price-btn-primary">
                <MessageCircle size={16} /> Get Started
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Reframe */}
      <section className="online-section online-section-dark">
        <div className="online-container online-reframe">
          <Zap size={32} className="online-reframe-icon" />
          <h2>Think about it this way.</h2>
          <p>You probably spend £5 a day on coffee. That's £150 a month on something that gives you a 20-minute buzz.</p>
          <p>For £320 a month, you get <strong>8 live PT sessions</strong>, a personalised programme, nutrition guidance, and someone in your corner making sure you actually follow through.</p>
          <p>That's the cost of two takeaways a week. Except instead of feeling sluggish afterwards, you'll be building the body and energy levels you've been promising yourself for years.</p>
          <p className="online-reframe-bold">Your gym membership costs you £40/month and you go twice. This costs the same per session — and you'll actually show up.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="online-section">
        <div className="online-container">
          <h2 className="online-section-title">Common Questions</h2>
          <div className="online-faq-list">
            {[
              { q: "Do I need equipment?", a: "Not necessarily. I design sessions around what you have — even if that's just bodyweight and a bit of floor space. Resistance bands and a set of dumbbells are helpful but not essential." },
              { q: "Is online training as effective as in-person?", a: "For most people, yes. You get the same coaching, programming and accountability. The only difference is I'm on a screen instead of standing next to you. Many of my online clients see faster results because the convenience means they never miss a session." },
              { q: "What if I travel for work?", a: "Perfect — that's one of the biggest advantages. Your sessions go wherever you go. Hotel room, Airbnb, your parents' house. No disruption to your programme." },
              { q: "I work shifts / irregular hours. Can you accommodate that?", a: "Absolutely. We'll find times that work around your schedule, not the other way around. Early mornings, lunch breaks, late evenings — I'm flexible." }
            ].map((item, i) => (
              <details key={i} className="online-faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="online-section online-final-cta">
        <div className="online-container" style={{ textAlign: 'center' }}>
          <h2>Stop waiting for the "right time".<br />It doesn't exist.</h2>
          <p>The right time is whenever you decide to start. And the easiest first step is a quick, no-pressure chat.</p>
          <a href="https://wa.me/447787166065?text=Hi%20Aaron%2C%20I%27m%20interested%20in%20online%20Zoom%20training.%20Can%20we%20have%20a%20quick%20chat%3F" target="_blank" rel="noopener noreferrer" className="online-cta-primary online-cta-final" data-testid="online-final-cta">
            <MessageCircle size={20} /> Message Aaron on WhatsApp
          </a>
          <p className="online-cta-note">Or book a free 10-minute call — no commitment, just a conversation.</p>
        </div>
      </section>

    </div>
  );
};

export default OnlineTrainingPage;
